/**
 * Card for a single BLE-discovered or manually-searched radio that has been
 * resolved to a real site location via the /radios/resolve/ endpoint.
 *
 * Tapping the card navigates to the site detail route so
 * useSiteRadioConnection can handle the auto-connect handshake. Staff also
 * get a secondary "Radio details" action that opens the radio detail page.
 *
 * Contains:
 * - NearbyUnitCard — resolved-radio card (default export)
 * - getSignalLabel — RSSI → human label + MUI color
 */

import AddLocationAltOutlinedIcon from '@mui/icons-material/AddLocationAltOutlined';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SignalCellularAltIcon from '@mui/icons-material/SignalCellularAlt';
import { Box, Button, Card, CardActionArea, CardContent, Chip, Stack, Typography } from '@mui/material';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { useUser } from '@sophocles/app';
import AssignOrCreateDialog from '@sophocles/components/dialogs/AssignOrCreateDialog';
import { refLabel } from '@sophocles/features/entities/references';
import { selectTenantInfo } from '@sophocles/features/tenant/slice';

/** RSSI thresholds (dBm) for signal-quality classification. */
const RSSI_EXCELLENT = -60;
const RSSI_GOOD = -70;
const RSSI_FAIR = -80;

/**
 * Map an RSSI value to a human label and MUI Chip color.
 *
 * @param {number|null|undefined} rssi
 * @param {Function} t - i18next translate function
 * @returns {{ label: string, color: string }}
 */
function getSignalLabel(rssi, t) {
  if (rssi == null) return { label: '—', color: 'default' };
  if (rssi >= RSSI_EXCELLENT) return { label: t('bluetooth.signal.excellent', 'Excellent'), color: 'success' };
  if (rssi >= RSSI_GOOD) return { label: t('bluetooth.signal.good', 'Good'), color: 'success' };
  if (rssi >= RSSI_FAIR) return { label: t('bluetooth.signal.fair', 'Fair'), color: 'warning' };
  return { label: t('bluetooth.signal.poor', 'Poor'), color: 'error' };
}

/**
 * @param {{
 *   resolution: {
 *     iccid: string,
 *     radio: { type: string, id: number, name: string } | null,
 *     location: {
 *       customer: { id: number, name: string },
 *       site_group: { id: number, name: string, description: string },
 *       site: { id: number, name: string, description: string }
 *     } | null,
 *     placement_type?: string,
 *     online?: boolean
 *   },
 *   rssi?: number,
 * }} props
 */
function NearbyUnitCard({ resolution, rssi }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const tenantInfo = useSelector(selectTenantInfo);
  const { isStaff, permissions } = useUser();
  const [isOnboardOpen, setOnboardOpen] = useState(false);

  const { location, online, radio } = resolution;
  const signal = getSignalLabel(rssi, t);
  // "Not on a site" covers everything except the onSite case — that
  // includes customer/group inventory rows where the onboarding path
  // is "pick a site (or create one) inside an existing scope".
  const isNotOnSite = !location?.site || resolution.placement_type !== 'onSite';
  // Permission to onboard an unassigned radio — mirrors the Inventory
  // Dashboard's assign gate.
  const canAssignToSite = isStaff || permissions?.isReseller || permissions?.isAtLeastSiteGroupAdmin;
  // A resolve entry with no radio is a "not found" state — there is nothing to
  // onboard until the radio exists in the system.
  const canOnboard = isNotOnSite && canAssignToSite && Boolean(radio?.id);

  const siteGroupTerm = tenantInfo.sitegroup_term
    ? t(tenantInfo.sitegroup_term, { defaultValue: tenantInfo.sitegroup_term })
    : t('search.siteGroup', 'Group');

  const handleCardClick = () => {
    if (isNotOnSite) return;
    const customerId = location?.customer?.id;
    const siteGroupId = location?.site_group?.id;
    const siteId = location?.site?.id;
    if (!customerId || !siteGroupId || !siteId) return;
    navigate(`/c/${customerId}/plant/${siteGroupId}/structure/${siteId}`, {
      state: { fromSearch: true },
    });
  };

  const handleViewRadioDetails = e => {
    e.stopPropagation();
    navigate(`/support/radios/${radio.id}`, { state: { fromSearch: true } });
  };

  // When the radio isn't on a site, fall back to ICCID as the title.
  const siteName = location?.site?.name ?? resolution.iccid;
  const secondaryText = (() => {
    if (location?.site) return `${location.customer.name} · ${refLabel(location.site_group)}`;
    if (location?.site_group) return `${location.customer?.name ?? ''} · ${refLabel(location.site_group)}`.trim();
    if (location?.customer) return location.customer.name;
    return t('search.noLocation', 'Not assigned to any site');
  })();

  return (
    <>
      <Card
        variant="outlined"
        sx={{
          mb: 'var(--space-2)',
          borderRadius: 'var(--card-radius)',
          borderColor: 'var(--card-border)',
          bgcolor: 'var(--card-bg)',
          transition:
            'box-shadow var(--duration-fast) var(--easing-standard), border-color var(--duration-normal) var(--easing-standard)',
          // Tappable cards (unit is on a site) get the stat-card "raised pad"
          // inset shadow; inventory cards that only expose the Onboard button
          // stay flat.
          ...(!isNotOnSite && {
            boxShadow: 'var(--card-shadow-raised)',
            '&:hover': { boxShadow: 'var(--card-shadow-raised-hover)' },
            '&:active': { boxShadow: 'var(--card-shadow-raised-active)' },
          }),
        }}
      >
        <CardActionArea onClick={handleCardClick} disabled={isNotOnSite}>
          <CardContent sx={{ p: 'var(--card-padding, var(--space-4))' }}>
            <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
              <Stack spacing={0.5} sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {online != null && (
                    <FiberManualRecordIcon
                      sx={{ fontSize: 10, color: online ? 'success.main' : 'error.main', flexShrink: 0 }}
                      aria-label={online ? t('radio.online', 'Online') : t('radio.offline', 'Offline')}
                    />
                  )}
                  <Typography variant="subtitle1" fontWeight={700} noWrap sx={{ color: 'var(--text-primary)' }}>
                    {siteName}
                  </Typography>
                </Box>
                <Typography
                  variant="body2"
                  noWrap
                  sx={{ color: 'var(--text-secondary)' }}
                  aria-label={
                    location?.site
                      ? `${location.customer.name} · ${siteGroupTerm}: ${refLabel(location.site_group)}`
                      : secondaryText
                  }
                >
                  {secondaryText}
                </Typography>
              </Stack>

              {rssi != null && (
                <Chip
                  icon={<SignalCellularAltIcon sx={{ fontSize: 14 }} />}
                  label={`${signal.label} (${rssi} dBm)`}
                  color={signal.color}
                  size="small"
                  variant="outlined"
                  sx={{ flexShrink: 0 }}
                />
              )}
            </Stack>
          </CardContent>
        </CardActionArea>
        {(canOnboard || (isStaff && radio?.id)) && (
          <Stack spacing={1} sx={{ px: 'var(--card-padding, var(--space-4))', pb: 'var(--space-3)' }}>
            {canOnboard && (
              <Button
                fullWidth
                variant="contained"
                size="small"
                startIcon={<AddLocationAltOutlinedIcon />}
                onClick={e => {
                  e.stopPropagation();
                  setOnboardOpen(true);
                }}
              >
                {t('search.onboardToSite', 'Onboard to a site')}
              </Button>
            )}
            {isStaff && radio?.id && (
              <Button
                variant="text"
                size="small"
                startIcon={<InfoOutlinedIcon />}
                onClick={handleViewRadioDetails}
                sx={{ alignSelf: 'flex-start', textTransform: 'none' }}
              >
                {t('search.radioDetails', 'Radio details')}
              </Button>
            )}
          </Stack>
        )}
      </Card>
      {canOnboard && (
        <AssignOrCreateDialog
          open={isOnboardOpen}
          onClose={() => setOnboardOpen(false)}
          radioId={radio.id}
          radioLabel={resolution.iccid || radio.name || `#${radio.id}`}
          customerId={location?.customer?.id || undefined}
          siteGroupId={location?.site_group?.id || undefined}
        />
      )}
    </>
  );
}

NearbyUnitCard.propTypes = {
  /** Resolved radio data from the /radios/resolve/ endpoint. */
  resolution: PropTypes.shape({
    iccid: PropTypes.string.isRequired,
    radio: PropTypes.shape({
      type: PropTypes.string,
      id: PropTypes.number,
      name: PropTypes.string,
    }),
    location: PropTypes.shape({
      customer: PropTypes.shape({ id: PropTypes.number, name: PropTypes.string }),
      site_group: PropTypes.shape({ id: PropTypes.number, name: PropTypes.string }),
      site: PropTypes.shape({ id: PropTypes.number, name: PropTypes.string }),
    }),
    placement_type: PropTypes.string,
    online: PropTypes.bool,
  }).isRequired,
  /** RSSI in dBm for signal-strength display; omit when unavailable. */
  rssi: PropTypes.number,
};

NearbyUnitCard.defaultProps = {
  rssi: undefined,
};

export default NearbyUnitCard;
